import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaWhatsapp, FaComments, FaPhoneAlt, FaPaperPlane, FaEdit, FaFileAlt, FaCheck, FaCheckDouble, FaSpinner } from 'react-icons/fa';
import { crmWhatsAppApi } from '../../api/crmApi';
import './CRM.css';

const fallbackConversations = [
  { id: 1, leadId: 1, name: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', mobile: '9876543210', lastMessage: 'Please send the quotation for 50 cylinders.', lastMessageAt: '2026-10-03T10:20:00', unread: 2, status: 'Open' },
  { id: 2, leadId: 2, name: 'Rajesh Kumar', company: 'Kumar Industrial Gases', mobile: '9811122233', lastMessage: 'Thanks, I will review and get back to you.', lastMessageAt: '2026-10-03T09:50:00', unread: 0, status: 'Open' },
  { id: 3, leadId: 3, name: 'Sunita Devi', company: 'Devi Gas Agency', mobile: '9765544332', lastMessage: 'Is the delivery possible by Friday?', lastMessageAt: '2026-10-02T17:35:00', unread: 1, status: 'Open' },
  { id: 4, leadId: 5, name: 'Pooja Sharma', company: 'Sharma Medical Gas', mobile: '9777666555', lastMessage: 'Brochure received 👍', lastMessageAt: '2026-10-01T14:10:00', unread: 0, status: 'Closed' },
];
const fallbackMessages = {
  1: [
    { id: 1, direction: 'out', text: 'Hello Ankit, this is Rahul from CTMS. Enquiry received for 50 oxygen cylinders.', sentAt: '2026-10-03T09:20:00', status: 'read' },
    { id: 2, direction: 'in', text: 'Yes, I had enquired. What would be the price per cylinder?', sentAt: '2026-10-03T09:35:00' },
    { id: 3, direction: 'out', text: 'It would be ₹1,650 per cylinder for 50+ quantity, excluding GST. I can send a formal quotation.', sentAt: '2026-10-03T09:40:00', status: 'read' },
    { id: 4, direction: 'in', text: 'Please send the quotation for 50 cylinders.', sentAt: '2026-10-03T10:20:00' },
  ],
  2: [
    { id: 1, direction: 'out', text: 'Hi Rajesh, sharing our LPG cylinder catalogue.', sentAt: '2026-10-03T09:45:00', status: 'read' },
    { id: 2, direction: 'in', text: 'Thanks, I will review and get back to you.', sentAt: '2026-10-03T09:50:00' },
  ],
  3: [
    { id: 1, direction: 'out', text: 'Hi Sunita, quotation QT-2041 has been shared for 20 acetylene cylinders.', sentAt: '2026-10-02T16:50:00', status: 'read' },
    { id: 2, direction: 'in', text: 'Is the delivery possible by Friday?', sentAt: '2026-10-02T17:35:00' },
  ],
  4: [
    { id: 1, direction: 'out', text: 'Hello Pooja, here is our medical gas product brochure.', sentAt: '2026-10-01T14:00:00', status: 'read' },
    { id: 2, direction: 'in', text: 'Brochure received 👍', sentAt: '2026-10-01T14:10:00' },
  ],
};
const fallbackTemplates = [
  { id: 1, name: 'Welcome', category: 'General', content: 'Hello {{customer_name}}, welcome to CTMS! ...', status: 'Active' },
  { id: 2, name: 'Quotation Follow-up', category: 'Quotation Reminder', content: 'Hi {{customer_name}}, gentle reminder on quotation {{quotation_number}}...', status: 'Active' },
  { id: 3, name: 'Payment Reminder', category: 'Payment Reminder', content: 'Dear {{customer_name}}, your payment for {{quotation_amount}} is due...', status: 'Active' },
];

const timeOf = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const dateLabel = (d) => { const t = new Date(d); const n = new Date(); return t.toDateString() === n.toDateString() ? 'Today' : t.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); };

export default function CRMWhatsApp() {
  const location = useLocation();
  const [conversations, setConversations] = useState(fallbackConversations);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates] = useState(fallbackTemplates);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await crmWhatsAppApi.getConversations({ size: 100 });
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      if (data.length) setConversations(data);
    } catch { /* fallback */ }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await crmWhatsAppApi.getMessages(convId);
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      if (data.length) { setMessages(data); return; }
    } catch { /* fallback */ }
    setMessages(fallbackMessages[convId] || []);
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => {
    if (location.state?.leadId) {
      const found = fallbackConversations.find((c) => c.leadId === location.state.leadId);
      if (found) selectConversation(found);
    }
  }, [location.state]);

  useEffect(() => { if (active) fetchMessages(active.id); }, [active, fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const selectConversation = (c) => {
    setActive(c);
    setConversations((p) => p.map((x) => x.id === c.id ? { ...x, unread: 0 } : x));
    try { crmWhatsAppApi.markAsRead(c.id); } catch { /* ignore */ }
  };

  const send = async () => {
    if (!draft.trim() || !active || sending) return;
    setSending(true);
    const temp = { id: Date.now(), direction: 'out', text: draft.trim(), sentAt: new Date().toISOString(), status: 'sent' };
    setMessages((p) => [...p, temp]);
    setDraft('');
    try {
      await crmWhatsAppApi.sendMessage({ conversationId: active.id, mobile: active.mobile, message: temp.text, leadId: active.leadId });
      toast.success('Message sent.');
    } catch {
      toast.error('Failed to send — check WhatsApp API config.');
      setMessages((p) => p.filter((m) => m.id !== temp.id));
      setDraft(temp.text);
    } finally { setSending(false); }
  };

  const sendTemplate = async (t) => {
    if (!active) return;
    let text = t.content
      .replaceAll('{{customer_name}}', active.name || '')
      .replaceAll('{{company_name}}', active.company || '');
    try {
      await crmWhatsAppApi.sendTemplate({ conversationId: active.id, templateId: t.id, mobile: active.mobile, leadId: active.leadId });
      setMessages((p) => [...p, { id: Date.now(), direction: 'out', text, sentAt: new Date().toISOString(), status: 'sent', template: t.name }]);
      toast.success(`Template "${t.name}" sent.`);
    } catch { toast.error('Failed to send template.'); }
    setShowTemplates(false);
  };

  const filtered = conversations.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) || (c.mobile || '').includes(q);
  });

  const grouped = messages.reduce((acc, m) => {
    const key = dateLabel(m.sentAt);
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaWhatsapp aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">WhatsApp Inbox</h1>
            <p className="crm-subtitle">Two-way WhatsApp conversations with leads & customers</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <span className="crm-badge crm-badge-won"><FaWhatsapp aria-hidden="true" /> WhatsApp Connected</span>
        </div>
      </div>

      <div className="crm-wa-layout">
        {/* CONVERSATION LIST */}
        <div className="crm-wa-list">
          <div className="crm-wa-list-search">
            <input
              type="text"
              aria-label="Search conversations"
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations..."
            />
          </div>
          <div className="crm-wa-list-items">
            {filtered.map((c) => (
              <div key={c.id} className={`crm-wa-list-item ${active?.id === c.id ? 'active' : ''}`} onClick={() => selectConversation(c)}>
                <div className="crm-wa-avatar">{(c.name || '?').charAt(0)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <strong style={{ fontSize: '0.83rem' }}>{c.name}</strong>
                    <span style={{ fontSize: '0.68rem', color: 'var(--muted)' }}>{timeOf(c.lastMessageAt)}</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{c.company}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.76rem', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.lastMessage}</span>
                    {c.unread > 0 && <span className="crm-wa-unread">{c.unread}</span>}
                  </div>
                </div>
              </div>
            ))}
            {!filtered.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: '0.82rem' }}>No conversations</div>}
          </div>
        </div>

        {/* CHAT AREA */}
        <div className="crm-wa-chat">
          {!active ? (
              <div className="crm-empty" style={{ height: '100%', border: 'none' }}>
                <div className="crm-empty-icon"><FaComments aria-hidden="true" /></div>
              <div className="crm-empty-title">Select a conversation</div>
              <div className="crm-empty-desc">Choose a chat from the left to start messaging.</div>
            </div>
          ) : (
            <>
              <div className="crm-wa-chat-head">
                <div className="crm-wa-avatar">{(active.name || '?').charAt(0)}</div>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: '0.86rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={active.name}>{active.name}</strong>
                  <div style={{ fontSize: '0.74rem', color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{active.company} · {active.mobile}</div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
                  <a className="crm-btn crm-btn-sm crm-btn-outline" href={`tel:${active.mobile}`}><FaPhoneAlt aria-hidden="true" /> Call</a>
                </div>
              </div>

              <div className="crm-wa-messages">
                {Object.entries(grouped).map(([day, msgs]) => (
                  <div key={day}>
                    <div className="crm-wa-day-divider"><span>{day}</span></div>
                    {msgs.map((m) => (
                      <div key={m.id} className={`crm-wa-msg ${m.direction}`}>
                        <div className="crm-wa-msg-bubble">
                          {m.template && <div style={{ fontSize: '0.66rem', opacity: 0.7, marginBottom: 3 }}><FaFileAlt aria-hidden="true" /> {m.template}</div>}
                          <div>{m.text}</div>
                          <div className="crm-wa-msg-time">
                            {timeOf(m.sentAt)}
                            {m.direction === 'out' && (
                              <span className="crm-wa-msg-status" title={m.status === 'read' ? 'Read' : 'Sent'}>
                                {m.status === 'read' ? <FaCheckDouble aria-hidden="true" /> : <FaCheck aria-hidden="true" />}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="crm-wa-input">
                {showTemplates && (
                  <div className="crm-wa-template-popup">
                    {templates.map((t) => (
                      <div key={t.id} className="crm-wa-template-item" onClick={() => sendTemplate(t)}>
                        <strong>{t.name}</strong>
                        <span>{t.content.slice(0, 70)}...</span>
                      </div>
                    ))}
                  </div>
                )}
                <button className="crm-btn crm-btn-sm crm-btn-outline" onClick={() => setShowTemplates((p) => !p)} title="Send template" aria-label="Send template"><FaEdit aria-hidden="true" /></button>
                <input type="text" aria-label="Message" autoComplete="off" value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && send()}
                  placeholder="Type a message..." />
                <button className="crm-btn crm-btn-primary" onClick={send} disabled={!draft.trim() || sending} aria-label="Send message">
                  {sending ? <FaSpinner className="crm-spin" aria-hidden="true" /> : <><FaPaperPlane aria-hidden="true" /> <span className="crm-wa-send-label">Send</span></>}
                </button>
              </div>
            </>
          )}
        </div>

        {/* RIGHT PANEL */}
        <div className="crm-wa-panel">
          {active ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="crm-panel-card">
                <div className="crm-panel-card-title">Contact Info</div>
                <div className="crm-info-row"><span>Name</span><strong>{active.name}</strong></div>
                <div className="crm-info-row"><span>Company</span><strong>{active.company || '—'}</strong></div>
                <div className="crm-info-row"><span>Mobile</span><strong>{active.mobile}</strong></div>
                <div className="crm-info-row"><span>Status</span><strong>{active.status || 'Open'}</strong></div>
              </div>
              <div className="crm-panel-card">
                <div className="crm-panel-card-title">Quick Actions</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button className="crm-btn crm-btn-outline crm-btn-sm" onClick={() => setShowTemplates((p) => !p)}><FaEdit aria-hidden="true" /> Send Template</button>
                  <a className="crm-btn crm-btn-outline crm-btn-sm" href={`tel:${active.mobile}`}><FaPhoneAlt aria-hidden="true" /> Call Now</a>
                </div>
              </div>
              <div className="crm-panel-card">
                <div className="crm-panel-card-title">Conversation Stats</div>
                <div className="crm-info-row"><span>Messages</span><strong>{messages.length}</strong></div>
                <div className="crm-info-row"><span>Sent by you</span><strong>{messages.filter((m) => m.direction === 'out').length}</strong></div>
                <div className="crm-info-row"><span>Received</span><strong>{messages.filter((m) => m.direction === 'in').length}</strong></div>
              </div>
            </div>
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: '0.82rem' }}>Select a chat to see details.</div>
          )}
        </div>
      </div>
    </div>
  );
}
